import React, { useMemo, useCallback } from 'react'
import { DateRangePicker, DatePicker } from 'rsuite'
import type { DateRange } from 'rsuite/esm/DateRangePicker'

export interface DateRangeValue {
  from?: number
  to?: number
}

// Поддержка разных форматов value: { from, to } или [Date, Date] или undefined
export type FilterDateRangeValue = DateRangeValue | DateRange | null | undefined

export interface FilterDateRangeProps {
  value?: FilterDateRangeValue
  onChange: (value: DateRangeValue) => void
  placeholder?: string
  format?: string
  showTime?: boolean
}

export interface FilterDateProps {
  value?: Date | number | null
  onChange: (value: Date | null) => void
  placeholder?: string
  format?: string
}

/** Фильтр диапазона дат */
export function FilterDateRange ({
  value,
  onChange,
  placeholder = 'Выберите период',
  format = 'dd.MM.yyyy',
  showTime = false
}: FilterDateRangeProps) {
  // Конвертируем value в DateRange для rsuite
  const dateRangeValue = useMemo<DateRange | null>(() => {
    if (!value) return null
    
    // Если это массив [Date, Date]
    if (Array.isArray(value)) {
      return value as DateRange
    }
    
    // Если это объект { from, to }
    if (value.from && value.to) {
      return [ new Date(value.from), new Date(value.to) ]
    }
    
    return null
  }, [ value ])

  // Если только from или только to - показываем кастомный placeholder
  const displayPlaceholder = useMemo(() => {
    if (!value || Array.isArray(value)) return placeholder
    
    if (value.from && !value.to) {
      const date = new Date(value.from)
      return `С ${date.toLocaleDateString('ru-RU')}`
    }
    
    if (!value.from && value.to) {
      const date = new Date(value.to)
      return `До ${date.toLocaleDateString('ru-RU')}`
    }
    
    return placeholder
  }, [ value, placeholder ])

  // Если есть частичное значение - показываем его в caretAs
  const hasPartialValue = value && !Array.isArray(value) && ((value.from && !value.to) || (!value.from && value.to))

  const handleChange = useCallback((range: DateRange | null) => {
    if (!range) {
      onChange({ from: undefined, to: undefined })
      return
    }
    const [ from, to ] = range
    
    // Устанавливаем время 00:00:00 для начала периода
    let fromTimestamp: number | undefined
    if (from) {
      const fromDate = new Date(from)
      fromDate.setHours(0, 0, 0, 0)
      fromTimestamp = fromDate.getTime()
    }
    
    // Устанавливаем время 23:59:59 для конца периода
    let toTimestamp: number | undefined
    if (to) {
      const toDate = new Date(to)
      toDate.setHours(23, 59, 59, 999)
      toTimestamp = toDate.getTime()
    }
    
    onChange({
      from: fromTimestamp,
      to: toTimestamp
    })
  }, [ onChange ])

  // Очистка частичного значения
  const handleClean = useCallback(() => {
    onChange({ from: undefined, to: undefined })
  }, [ onChange ])

  return (
    <DateRangePicker
      value={dateRangeValue}
      onChange={handleChange}
      onClean={handleClean}
      placeholder={hasPartialValue ? displayPlaceholder : placeholder}
      size="sm"
      format={showTime ? `${format} HH:mm` : format}
      showMeridiem={false}
      block
      cleanable={!!dateRangeValue || hasPartialValue}
    />
  )
}

/** Фильтр одной даты */
export function FilterDate ({
  value,
  onChange,
  placeholder = 'Выберите дату',
  format = 'dd.MM.yyyy'
}: FilterDateProps) {
  const dateValue = useMemo(
    () => {
      if (!value) return undefined
      if (value instanceof Date) return value
      return new Date(value)
    },
    [ value ]
  )

  const handleChange = useCallback((date: Date | null) => {
    onChange(date)
  }, [ onChange ])

  return (
    <DatePicker
      value={dateValue}
      onChange={handleChange}
      onClean={() => handleChange(null)}
      placeholder={placeholder}
      size="sm"
      format={format}
      block
    />
  )
}
